import { useState, useMemo } from 'react'
import {
  Navbar,
  Block,
  BlockTitle,
  List,
  ListItem,
  Sheet,
  Button,
  Dialog,
  DialogButton,
} from 'konsta/react'
import {
  Lock,
  Unlock,
  Clock,
  CheckCircle2,
  Hash,
  DoorOpen,
  DollarSign,
  Calendar,
  AlertTriangle,
  FileText,
  ArrowRight,
  Scale,
  Eye,
  GitCompare,
} from 'lucide-react'
import { formatCurrency, formatRelativeTime, cn } from '@/lib/utils'

// --- Types ---

type FrozenPotStatus = 'frozen' | 'released'

interface FrozenPot {
  id: string
  handId: string
  room: string
  amount: number
  reason: string
  status: FrozenPotStatus
  date: Date
  evalA: string
  evalB: string
  resolution?: string
}

// --- Mock Data ---

const mockFrozenPots: FrozenPot[] = [
  {
    id: 'FP001',
    handId: 'H045',
    room: '高額桌 VIP',
    amount: 250000,
    reason: '評估器結果不一致',
    status: 'frozen',
    date: new Date(Date.now() - 1000 * 60 * 60 * 2),
    evalA: '同花順',
    evalB: '順子',
  },
  {
    id: 'FP002',
    handId: 'H052',
    room: '中級桌 #3',
    amount: 80000,
    reason: '評估器結果不一致',
    status: 'frozen',
    date: new Date(Date.now() - 1000 * 60 * 60 * 12),
    evalA: '葫蘆',
    evalB: '葫蘆 (不同排名)',
  },
  {
    id: 'FP003',
    handId: 'H038',
    room: '新手桌 #1',
    amount: 12000,
    reason: '手動凍結 - 玩家爭議',
    status: 'released',
    date: new Date(Date.now() - 1000 * 60 * 60 * 48),
    evalA: '兩對',
    evalB: '兩對',
    resolution: '經人工驗證，評估器 A 結果正確。',
  },
]

// --- Helpers ---

const statusConfig: Record<FrozenPotStatus, { label: string; color: string; bgColor: string }> = {
  frozen: { label: '凍結中', color: '#FF3B30', bgColor: 'bg-[#FF3B30]/10' },
  released: { label: '已釋放', color: '#34C759', bgColor: 'bg-[#34C759]/10' },
}

function StatusBadge({ status }: { status: FrozenPotStatus }) {
  const config = statusConfig[status]
  const Icon = status === 'frozen' ? Lock : Unlock
  return (
    <span
      className={cn(
        'inline-flex items-center gap-1 text-xs font-semibold px-2 py-0.5 rounded-full',
        config.bgColor
      )}
      style={{ color: config.color }}
    >
      <Icon className="w-3 h-3" />
      {config.label}
    </span>
  )
}

// --- Detail Sheet ---

function FrozenPotDetailSheet({
  pot,
  open,
  onClose,
  onRelease,
}: {
  pot: FrozenPot | null
  open: boolean
  onClose: () => void
  onRelease: () => void
}) {
  if (!pot) return null

  return (
    <Sheet
      opened={open}
      onBackdropClick={onClose}
      className="pb-safe"
      style={{ height: 'auto', maxHeight: '90vh' }}
    >
      <div className="overflow-y-auto" style={{ maxHeight: '85vh' }}>
        {/* Header */}
        <div className="flex items-center justify-between px-4 py-3 border-b border-[#E5E5EA]">
          <h3 className="text-lg font-bold text-black">凍結底池詳情</h3>
          <button onClick={onClose} className="text-sm font-medium text-[#007AFF]">
            關閉
          </button>
        </div>

        {/* Pot Info */}
        <Block className="!mt-2">
          <div className="flex items-center justify-between mb-3">
            <span className="text-base font-bold text-black">{pot.id}</span>
            <StatusBadge status={pot.status} />
          </div>

          <div className="space-y-2.5">
            <div className="flex items-center gap-2 text-sm">
              <Hash className="w-4 h-4 text-[#8E8E93]" />
              <span className="text-[#8E8E93]">手牌 ID</span>
              <span className="font-medium text-black ml-auto">{pot.handId}</span>
            </div>
            <div className="flex items-center gap-2 text-sm">
              <DoorOpen className="w-4 h-4 text-[#8E8E93]" />
              <span className="text-[#8E8E93]">房間</span>
              <span className="font-medium text-black ml-auto">{pot.room}</span>
            </div>
            <div className="flex items-center gap-2 text-sm">
              <DollarSign className="w-4 h-4 text-[#8E8E93]" />
              <span className="text-[#8E8E93]">凍結金額</span>
              <span className="font-semibold text-[#FF3B30] ml-auto">
                {formatCurrency(pot.amount)}
              </span>
            </div>
            <div className="flex items-center gap-2 text-sm">
              <AlertTriangle className="w-4 h-4 text-[#8E8E93]" />
              <span className="text-[#8E8E93]">凍結原因</span>
              <span className="font-medium text-black ml-auto">{pot.reason}</span>
            </div>
            <div className="flex items-center gap-2 text-sm">
              <Calendar className="w-4 h-4 text-[#8E8E93]" />
              <span className="text-[#8E8E93]">凍結時間</span>
              <span className="font-medium text-black ml-auto">
                {formatRelativeTime(pot.date)}
              </span>
            </div>
          </div>
        </Block>

        {/* Evaluator Comparison */}
        <BlockTitle className="flex items-center gap-1.5">
          <GitCompare className="w-4 h-4" />
          評估器結果比較
        </BlockTitle>
        <Block strong inset>
          <div className="space-y-3">
            <div className="flex items-center justify-between p-3 rounded-lg bg-[#F2F2F7]">
              <div>
                <p className="text-xs text-[#8E8E93] font-medium">評估器 A (pokersolver)</p>
                <p className="text-sm font-bold text-black mt-0.5">{pot.evalA}</p>
              </div>
              <div className={cn(
                'w-8 h-8 rounded-full flex items-center justify-center',
                pot.evalA === pot.evalB ? 'bg-[#34C759]/10' : 'bg-[#FF3B30]/10'
              )}>
                {pot.evalA === pot.evalB ? (
                  <CheckCircle2 className="w-4 h-4 text-[#34C759]" />
                ) : (
                  <AlertTriangle className="w-4 h-4 text-[#FF3B30]" />
                )}
              </div>
            </div>
            <div className="flex items-center justify-between p-3 rounded-lg bg-[#F2F2F7]">
              <div>
                <p className="text-xs text-[#8E8E93] font-medium">評估器 B (poker-evaluator)</p>
                <p className="text-sm font-bold text-black mt-0.5">{pot.evalB}</p>
              </div>
              {pot.evalA !== pot.evalB && (
                <span className="text-xs font-semibold text-[#FF3B30] bg-[#FF3B30]/10 px-2 py-0.5 rounded-full">
                  不一致
                </span>
              )}
            </div>
          </div>
        </Block>

        {/* Hand Replay */}
        <BlockTitle className="flex items-center gap-1.5">
          <Eye className="w-4 h-4" />
          手牌回放
        </BlockTitle>
        <Block strong inset>
          <div className="space-y-2">
            <div className="flex items-center gap-2 text-sm">
              <FileText className="w-4 h-4 text-[#007AFF]" />
              <span className="text-[#007AFF] font-medium">查看完整手牌記錄</span>
              <ArrowRight className="w-3 h-3 text-[#8E8E93] ml-auto" />
            </div>
            <div className="flex items-center gap-2 text-sm">
              <Scale className="w-4 h-4 text-[#007AFF]" />
              <span className="text-[#007AFF] font-medium">查看底池計算明細</span>
              <ArrowRight className="w-3 h-3 text-[#8E8E93] ml-auto" />
            </div>
            <div className="flex items-center gap-2 text-sm">
              <FileText className="w-4 h-4 text-[#007AFF]" />
              <span className="text-[#007AFF] font-medium">PokerKit 驗證結果</span>
              <ArrowRight className="w-3 h-3 text-[#8E8E93] ml-auto" />
            </div>
          </div>
        </Block>

        {/* Resolution */}
        {pot.status === 'released' && pot.resolution && (
          <>
            <BlockTitle>處理結果</BlockTitle>
            <Block strong inset>
              <p className="text-sm text-[#34C759] font-medium">{pot.resolution}</p>
            </Block>
          </>
        )}

        {/* Actions - only for frozen pots */}
        {pot.status === 'frozen' && (
          <Block className="!px-4 !mt-4 !mb-4">
            <div className="flex gap-3">
              <Button className="flex-1" large rounded onClick={onRelease}>
                <span className="flex items-center gap-2">
                  <CheckCircle2 className="w-4 h-4" />
                  以評估器 A 釋放
                </span>
              </Button>
              <Button className="flex-1" large rounded tonal onClick={onRelease}>
                <span className="flex items-center gap-2">
                  <Scale className="w-4 h-4" />
                  以評估器 B 釋放
                </span>
              </Button>
            </div>
          </Block>
        )}

        <div className="h-4" />
      </div>
    </Sheet>
  )
}

// --- Main Screen ---

export function AdminFrozenPotsScreen() {
  const [selectedPot, setSelectedPot] = useState<FrozenPot | null>(null)
  const [sheetOpen, setSheetOpen] = useState(false)
  const [dialogOpen, setDialogOpen] = useState(false)

  const summary = useMemo(() => {
    const frozen = mockFrozenPots.filter((p) => p.status === 'frozen')
    const released = mockFrozenPots.filter((p) => p.status === 'released')
    const totalAmount = mockFrozenPots.reduce((sum, p) => sum + p.amount, 0)
    return {
      total: mockFrozenPots.length,
      totalAmount,
      frozen: frozen.length,
      released: released.length,
    }
  }, [])

  const handlePotClick = (pot: FrozenPot) => {
    setSelectedPot(pot)
    setSheetOpen(true)
  }

  const closeSheet = () => {
    setSheetOpen(false)
    setSelectedPot(null)
  }

  const handleRelease = () => {
    setDialogOpen(true)
  }

  const confirmRelease = () => {
    setDialogOpen(false)
    closeSheet()
  }

  return (
    <>
      <Navbar title="凍結底池" />

      <div className="overflow-y-auto pb-8">
        {/* Summary Cards */}
        <Block className="!mt-4 !mb-2">
          <div className="grid grid-cols-4 gap-2">
            <div className="bg-white rounded-xl p-3 text-center">
              <p className="text-xl font-bold text-black">{summary.total}</p>
              <p className="text-[10px] text-[#8E8E93] mt-0.5">凍結總數</p>
            </div>
            <div className="bg-white rounded-xl p-3 text-center">
              <p className="text-xl font-bold text-[#FF3B30]">
                {formatCurrency(summary.totalAmount).replace('$', '')}
              </p>
              <p className="text-[10px] text-[#8E8E93] mt-0.5">總金額</p>
            </div>
            <div className="bg-white rounded-xl p-3 text-center">
              <p className="text-xl font-bold text-[#FF9500]">{summary.frozen}</p>
              <p className="text-[10px] text-[#8E8E93] mt-0.5">待處理</p>
            </div>
            <div className="bg-white rounded-xl p-3 text-center">
              <p className="text-xl font-bold text-[#34C759]">{summary.released}</p>
              <p className="text-[10px] text-[#8E8E93] mt-0.5">已釋放</p>
            </div>
          </div>
        </Block>

        {/* Frozen Pots List */}
        <BlockTitle>凍結底池列表</BlockTitle>
        <List strongIos insetIos>
          {mockFrozenPots.map((pot) => (
            <ListItem
              key={pot.id}
              link
              chevronIos
              onClick={() => handlePotClick(pot)}
              title={
                <span className="font-semibold text-black">
                  {pot.handId} - {pot.room}
                </span>
              }
              subtitle={
                <span className="flex items-center gap-2 text-[#8E8E93]">
                  <span className="flex items-center gap-1">
                    <AlertTriangle className="w-3 h-3" />
                    {pot.reason}
                  </span>
                </span>
              }
              after={<StatusBadge status={pot.status} />}
              media={
                <div
                  className={cn(
                    'w-10 h-10 rounded-xl flex items-center justify-center',
                    pot.status === 'frozen' ? 'bg-[#FF3B30]/10' : 'bg-[#34C759]/10'
                  )}
                >
                  {pot.status === 'frozen' ? (
                    <Lock className="w-5 h-5 text-[#FF3B30]" />
                  ) : (
                    <Unlock className="w-5 h-5 text-[#34C759]" />
                  )}
                </div>
              }
              text={
                <span className="flex items-center gap-3 text-[#8E8E93]">
                  <span className="flex items-center gap-1 text-[#FF3B30] font-semibold">
                    <DollarSign className="w-3 h-3" />
                    {formatCurrency(pot.amount)}
                  </span>
                  <span className="flex items-center gap-1">
                    <Clock className="w-3 h-3" />
                    {formatRelativeTime(pot.date)}
                  </span>
                </span>
              }
            />
          ))}
        </List>

        {/* Evaluator Info */}
        <BlockTitle>評估器說明</BlockTitle>
        <Block strong inset>
          <div className="space-y-3 text-sm">
            <p className="text-[#8E8E93] leading-relaxed">
              系統使用兩個獨立的手牌評估器進行冗餘驗證。當兩個評估器結果不一致時，底池將自動凍結，等待管理員人工驗證。
            </p>
            <div className="flex items-center gap-2 p-2.5 rounded-lg bg-[#F2F2F7]">
              <div className="w-6 h-6 rounded-full bg-[#007AFF]/10 flex items-center justify-center">
                <span className="text-xs font-bold text-[#007AFF]">A</span>
              </div>
              <div>
                <p className="font-medium text-black">pokersolver</p>
                <p className="text-xs text-[#8E8E93]">獨立排名演算法</p>
              </div>
            </div>
            <div className="flex items-center gap-2 p-2.5 rounded-lg bg-[#F2F2F7]">
              <div className="w-6 h-6 rounded-full bg-[#AF52DE]/10 flex items-center justify-center">
                <span className="text-xs font-bold text-[#AF52DE]">B</span>
              </div>
              <div>
                <p className="font-medium text-black">poker-evaluator</p>
                <p className="text-xs text-[#8E8E93]">Two Plus Two 查找表演算法</p>
              </div>
            </div>
          </div>
        </Block>
      </div>

      {/* Detail Sheet */}
      <FrozenPotDetailSheet
        pot={selectedPot}
        open={sheetOpen}
        onClose={closeSheet}
        onRelease={handleRelease}
      />

      {/* Release Confirmation Dialog */}
      <Dialog
        opened={dialogOpen}
        onBackdropClick={() => setDialogOpen(false)}
        title="確認釋放底池"
        content={`確定釋放凍結底池 ${selectedPot ? formatCurrency(selectedPot.amount) : ''}？資金將按照選定的評估結果分配給對應的贏家。此操作無法撤銷。`}
        buttons={
          <>
            <DialogButton onClick={() => setDialogOpen(false)}>取消</DialogButton>
            <DialogButton strong onClick={confirmRelease}>
              確認釋放
            </DialogButton>
          </>
        }
      />
    </>
  )
}
